'use client';

import React, { createContext, useContext, useState, ReactNode } from 'react';

export type Language = 'tr' | 'en' | 'ru' | 'es';

export interface Translations {
  // Nav / Footer
  nav_projects: string;
  nav_experience: string;
  nav_contact: string;
  nav_cv: string;

  // Hero
  hero_greeting: string;
  hero_title: string;
  hero_subtitle: string;
  hero_bio: string;
  hero_cta_projects: string;
  hero_cta_contact: string;
  hero_stat_years: string;
  hero_stat_companies: string;
  hero_stat_stack: string;
  hero_stat_stack_sub: string;
  hero_open: string;

  // Skills
  skills_label: string;
  skills_heading_1: string;
  skills_heading_2: string;
  skills_subtitle: string;
  skills_backend_title: string;
  skills_backend_desc: string;
  skills_frontend_title: string;
  skills_frontend_desc: string;
  skills_db_title: string;
  skills_db_desc: string;

  // Projects
  projects_label: string;
  projects_heading_1: string;
  projects_heading_2: string;
  projects_view_linkedin: string;
  projects_featured: string;
  projects_status_done: string;
  projects_status_active: string;

  // Project titles & descriptions
  proj1_title: string;
  proj1_desc: string;
  proj2_title: string;
  proj2_desc: string;
  proj3_title: string;
  proj3_desc: string;
  proj4_title: string;
  proj4_desc: string;

  // Experience
  exp_label: string;
  exp_heading_1: string;
  exp_heading_2: string;
  exp_current_badge: string;
  exp_fulltime: string;
  exp_hybrid: string;
  exp_freelance: string;
  exp_istanbul: string;
  exp_duzce: string;

  exp1_role: string;
  exp1_desc: string;
  exp2_role: string;
  exp2_desc: string;
  exp3_role: string;
  exp3_desc: string;
  exp4_role: string;
  exp4_desc: string;
  exp5_role: string;
  exp5_desc: string;

  // Contact
  contact_label: string;
  contact_heading_1: string;
  contact_heading_2: string;
  contact_subtitle: string;
  contact_quote: string;
  contact_available: string;
  contact_response: string;
  contact_location: string;
  contact_cv_handle: string;

  // Footer
  footer_copyright: string;
}

const translations: Record<Language, Translations> = {
  tr: {
    nav_projects: 'Projeler',
    nav_experience: 'Deneyim',
    nav_contact: 'İletişim',
    nav_cv: 'CV',

    hero_greeting: 'Merhaba, ben',
    hero_title: 'Kerim Şahin',
    hero_subtitle: 'Backend & Frontend Senior Developer',
    hero_bio:
      '14 yıllık yazılım geliştirme deneyimiyle C#, .NET Core, Blazor, Angular ve JavaScript alanlarında uzmanlaşmış Full Stack Developer. SaaS geliştirme, veritabanı tasarımı, API entegrasyonu ve mikroservis mimarisinde kapsamlı bilgiye sahip. Takım liderliği, Agile proje yönetimi ve mentörlük konularında güçlü beceriler.',
    hero_cta_projects: 'Projeleri Gör',
    hero_cta_contact: 'İletişime Geç',
    hero_stat_years: 'Yıl deneyim',
    hero_stat_companies: 'Şirkette çalışıldı',
    hero_stat_stack: 'Full Stack',
    hero_stat_stack_sub: 'Backend & Frontend',
    hero_open: 'Fırsatlara açık',

    skills_label: 'Teknoloji Yığını',
    skills_heading_1: 'Kullandığım',
    skills_heading_2: 'araçlar.',
    skills_subtitle: 'Prodüksiyon sistemleri geliştirirken tercih ettiğim teknolojiler ve araçlar.',
    skills_backend_title: 'Backend Geliştirme',
    skills_backend_desc:
      '.NET Core Web API, mikroservis mimarisi, Entity Framework, RESTful API tasarımı ve performans optimizasyonu.',
    skills_frontend_title: 'Frontend Geliştirme',
    skills_frontend_desc:
      'Angular, Blazor, React ile modern ve kullanıcı dostu arayüzler. Responsive tasarım ve performans odaklı geliştirme.',
    skills_db_title: 'Veritabanı & DevOps',
    skills_db_desc:
      'SQL Server, PostgreSQL, MongoDB, Redis ile veritabanı tasarımı. Docker, Azure DevOps ve Git ile CI/CD süreçleri.',

    projects_label: 'Öne Çıkan Çalışmalar',
    projects_heading_1: 'Geliştirdiğim',
    projects_heading_2: 'projeler.',
    projects_view_linkedin: "LinkedIn'de Görüntüle",
    projects_featured: 'Öne Çıkan',
    projects_status_done: 'Tamamlandı',
    projects_status_active: 'Aktif',

    proj1_title: 'Diriliş Ertuğrul Dijital Medya Platformu',
    proj1_desc:
      'MOPP Ajans bünyesinde geliştirilen, Diriliş Ertuğrul dizisine ait dijital medya platformu. ASP.NET MVC ile backend, SQL Server ile veritabanı yönetimi gerçekleştirildi. SEO optimizasyonu ve performans iyileştirmeleri yapıldı.',
    proj2_title: 'Kurumsal Web Uygulamaları — Real Solutions',
    proj2_desc:
      '.NET Core Web API ile backend servisleri, Angular ile frontend arayüzleri geliştirildi. Ölçeklenebilir mimari tasarımı yapıldı, junior geliştiricilere mentörlük sağlandı. API entegrasyonları, güvenlik kontrolleri ve performans iyileştirmeleri gerçekleştirildi.',
    proj3_title: 'KOBİ Yazılım Çözümleri — Freelance',
    proj3_desc:
      'Küçük ve orta ölçekli işletmeler için özel yazılım çözümleri geliştirildi. Web tabanlı uygulamalar, API entegrasyonları ve veri işleme projeleri tamamlandı. Müşteri ihtiyaçlarına göre kullanıcı dostu arayüzler tasarlandı.',
    proj4_title: 'Kurumsal Web Uygulamaları — E2 Teknolojileri',
    proj4_desc:
      'Kurumsal web uygulamaları için backend ve frontend geliştirme. Veritabanı tasarımı ve performans iyileştirmeleri. RESTful API geliştirme ve üçüncü taraf sistem entegrasyonları sağlandı.',

    exp_label: 'Deneyim',
    exp_heading_1: 'Çalıştığım',
    exp_heading_2: 'yerler.',
    exp_current_badge: 'Son Pozisyon',
    exp_fulltime: 'Tam Zamanlı',
    exp_hybrid: 'Hibrit',
    exp_freelance: 'Freelance',
    exp_istanbul: 'İstanbul',
    exp_duzce: 'Düzce',

    exp1_role: 'Yazılım Geliştirme Uzmanı',
    exp1_desc:
      '.NET Core Web API ile backend servisleri oluşturuldu, Angular ile frontend arayüzleri geliştirildi. Kurumsal ölçekli proje geliştirmede modern web teknolojileriyle ölçeklenebilir mimariler tasarlandı. Junior geliştiricilere mentörlük sağlanırken takım koordinasyonu yürütüldü. API entegrasyonları, güvenlik kontrolleri ve performans iyileştirmeleri gerçekleştirildi.',
    exp2_role: 'Freelance Yazılım Geliştirici',
    exp2_desc:
      'Küçük ve orta ölçekli işletmeler için özel yazılım çözümleri geliştirildi. Web tabanlı uygulamalar, API entegrasyonları ve veri işleme projeleri tamamlandı. Müşteri ihtiyaçlarına göre kullanıcı dostu arayüzler tasarlandı.',
    exp3_role: 'Full Stack Developer',
    exp3_desc:
      'ASP.NET MVC kullanarak backend geliştirme yapıldı, SQL Server ile veritabanları yönetildi ve Diriliş Ertuğrul Dijital Medya Platformu geliştirildi. Proje yönetimi ve Agile metodolojileriyle takım koordinasyonu sağlandı. SEO ve performans optimizasyon çalışmaları yürütüldü.',
    exp4_role: 'Full Stack Developer',
    exp4_desc:
      'Kurumsal web uygulamaları için backend ve frontend geliştirme yapıldı. Veritabanı tasarımı ve performans iyileştirmeleri gerçekleştirildi. RESTful API geliştirme ve üçüncü taraf sistem entegrasyonları sağlandı.',
    exp5_role: 'Frontend Developer',
    exp5_desc:
      'Web tabanlı arayüzler geliştirildi ve optimize edildi. HTML/CSS ve JavaScript ile responsive tasarımlar oluşturuldu. Mevcut projelerde bakım ve hata düzeltme çalışmalarına katkıda bulunuldu.',

    contact_label: 'İletişim',
    contact_heading_1: 'Birlikte bir şeyler',
    contact_heading_2: 'üretelim.',
    contact_subtitle:
      'Tam zamanlı bir pozisyon, freelance proje veya sadece ilginç problemler hakkında bir sohbet olsun — mesajınızı bekliyorum.',
    contact_quote:
      '"14 yıllık deneyimimle anlamlı teknik kararlar alabileceğim, güçlü bir ekiple çalışabileceğim ve insanların gerçekten kullandığı şeyler üretebileceğim rollere odaklanıyorum. Düzce merkezli, hibrit çalışmaya açığım."',
    contact_available: 'Fırsatlara açık',
    contact_response: 'Genellikle 24 saat içinde yanıt verir. Konum: Düzce, Türkiye.',
    contact_location: 'Düzce, Türkiye',
    contact_cv_handle: 'PDF İndir',

    footer_copyright: '© 2026 Kerim Şahin',
  },

  en: {
    nav_projects: 'Projects',
    nav_experience: 'Experience',
    nav_contact: 'Contact',
    nav_cv: 'CV',

    hero_greeting: "Hi, I'm",
    hero_title: 'Kerim Şahin',
    hero_subtitle: 'Backend & Frontend Senior Developer',
    hero_bio:
      'Full Stack Developer with 14 years of software development experience, specializing in C#, .NET Core, Blazor, Angular, and JavaScript. Extensive knowledge in SaaS development, database design, API integration, and microservice architecture. Strong skills in team leadership, Agile project management, and mentoring.',
    hero_cta_projects: 'View Projects',
    hero_cta_contact: 'Get in Touch',
    hero_stat_years: 'Years of experience',
    hero_stat_companies: 'Companies worked at',
    hero_stat_stack: 'Full Stack',
    hero_stat_stack_sub: 'Backend & Frontend',
    hero_open: 'Open to opportunities',

    skills_label: 'Tech Stack',
    skills_heading_1: 'Tools I',
    skills_heading_2: 'work with.',
    skills_subtitle: 'Technologies and tools I prefer when building production systems.',
    skills_backend_title: 'Backend Development',
    skills_backend_desc:
      '.NET Core Web API, microservice architecture, Entity Framework, RESTful API design, and performance optimization.',
    skills_frontend_title: 'Frontend Development',
    skills_frontend_desc:
      'Modern, user-friendly interfaces with Angular, Blazor, and React. Responsive design and performance-driven development.',
    skills_db_title: 'Database & DevOps',
    skills_db_desc:
      'Database design with SQL Server, PostgreSQL, MongoDB, and Redis. CI/CD pipelines with Docker, Azure DevOps, and Git.',

    projects_label: 'Featured Work',
    projects_heading_1: 'Projects I have',
    projects_heading_2: 'built.',
    projects_view_linkedin: 'View on LinkedIn',
    projects_featured: 'Featured',
    projects_status_done: 'Completed',
    projects_status_active: 'Active',

    proj1_title: 'Diriliş Ertuğrul Digital Media Platform',
    proj1_desc:
      'A digital media platform for the Diriliş Ertuğrul TV series, developed at MOPP Agency. Backend built with ASP.NET MVC, database management with SQL Server. SEO optimization and performance improvements were implemented.',
    proj2_title: 'Enterprise Web Applications — Real Solutions',
    proj2_desc:
      'Backend services built with .NET Core Web API, frontend interfaces developed with Angular. Scalable architecture designed for enterprise-scale projects, mentoring provided to junior developers. API integrations, security controls, and performance improvements delivered.',
    proj3_title: 'SME Software Solutions — Freelance',
    proj3_desc:
      'Custom software solutions developed for small and medium-sized businesses. Web-based applications, API integrations, and data processing projects completed. User-friendly interfaces designed according to client needs.',
    proj4_title: 'Enterprise Web Applications — E2 Technologies',
    proj4_desc:
      'Backend and frontend development for enterprise web applications. Database design and performance improvements. RESTful API development and third-party system integrations provided.',

    exp_label: 'Experience',
    exp_heading_1: 'Places I have',
    exp_heading_2: 'worked.',
    exp_current_badge: 'Latest Position',
    exp_fulltime: 'Full-Time',
    exp_hybrid: 'Hybrid',
    exp_freelance: 'Freelance',
    exp_istanbul: 'Istanbul',
    exp_duzce: 'Düzce',

    exp1_role: 'Software Development Specialist',
    exp1_desc:
      'Backend services built with .NET Core Web API, frontend interfaces developed with Angular. Scalable architectures designed with modern web technologies for enterprise-scale projects. Team coordination managed while mentoring junior developers. API integrations, security controls, and performance improvements delivered.',
    exp2_role: 'Freelance Software Developer',
    exp2_desc:
      'Custom software solutions developed for small and medium-sized businesses. Web-based applications, API integrations, and data processing projects completed. User-friendly interfaces designed according to client needs.',
    exp3_role: 'Full Stack Developer',
    exp3_desc:
      'Backend development using ASP.NET MVC, database management with SQL Server, and development of the Diriliş Ertuğrul Digital Media Platform. Team coordination ensured through project management and Agile methodologies. SEO and performance optimization work carried out.',
    exp4_role: 'Full Stack Developer',
    exp4_desc:
      'Backend and frontend development for enterprise web applications. Database design and performance improvements implemented. RESTful API development and third-party system integrations provided.',
    exp5_role: 'Frontend Developer',
    exp5_desc:
      'Web-based interfaces developed and optimized. Responsive designs created with HTML/CSS and JavaScript. Contributed to maintenance and bug-fixing work on existing projects.',

    contact_label: 'Contact',
    contact_heading_1: "Let's build something",
    contact_heading_2: 'together.',
    contact_subtitle:
      'Whether it is a full-time position, a freelance project, or just a conversation about interesting problems — I look forward to hearing from you.',
    contact_quote:
      '"With 14 years of experience, I focus on roles where I can make meaningful technical decisions, work with a strong team, and build things people actually use. Based in Düzce, open to hybrid work."',
    contact_available: 'Open to opportunities',
    contact_response: 'Usually responds within 24 hours. Location: Düzce, Turkey.',
    contact_location: 'Düzce, Turkey',
    contact_cv_handle: 'Download PDF',

    footer_copyright: '© 2026 Kerim Şahin',
  },

  ru: {
    nav_projects: 'Проекты',
    nav_experience: 'Опыт',
    nav_contact: 'Контакты',
    nav_cv: 'Резюме',

    hero_greeting: 'Привет, я',
    hero_title: 'Kerim Şahin',
    hero_subtitle: 'Старший Backend & Frontend разработчик',
    hero_bio:
      'Full Stack разработчик с 14-летним опытом в разработке программного обеспечения, специализирующийся на C#, .NET Core, Blazor, Angular и JavaScript. Обширные знания в области разработки SaaS, проектирования баз данных, интеграции API и микросервисной архитектуры. Сильные навыки в руководстве командой, Agile-управлении проектами и наставничестве.',
    hero_cta_projects: 'Смотреть проекты',
    hero_cta_contact: 'Связаться',
    hero_stat_years: 'Лет опыта',
    hero_stat_companies: 'Компаний',
    hero_stat_stack: 'Full Stack',
    hero_stat_stack_sub: 'Backend & Frontend',
    hero_open: 'Открыт к предложениям',

    skills_label: 'Технологии',
    skills_heading_1: 'Инструменты,',
    skills_heading_2: 'которые я использую.',
    skills_subtitle:
      'Технологии и инструменты, которые я предпочитаю при разработке production-систем.',
    skills_backend_title: 'Backend-разработка',
    skills_backend_desc:
      '.NET Core Web API, микросервисная архитектура, Entity Framework, проектирование RESTful API и оптимизация производительности.',
    skills_frontend_title: 'Frontend-разработка',
    skills_frontend_desc:
      'Современные, удобные интерфейсы с Angular, Blazor и React. Адаптивный дизайн и разработка с упором на производительность.',
    skills_db_title: 'Базы данных & DevOps',
    skills_db_desc:
      'Проектирование баз данных с SQL Server, PostgreSQL, MongoDB и Redis. CI/CD-пайплайны с Docker, Azure DevOps и Git.',

    projects_label: 'Избранные работы',
    projects_heading_1: 'Проекты,',
    projects_heading_2: 'которые я создал.',
    projects_view_linkedin: 'Смотреть на LinkedIn',
    projects_featured: 'Избранное',
    projects_status_done: 'Завершён',
    projects_status_active: 'Активный',

    proj1_title: 'Цифровая медиаплатформа «Diriliş Ertuğrul»',
    proj1_desc:
      'Цифровая медиаплатформа для сериала «Diriliş Ertuğrul», разработанная в агентстве MOPP. Backend создан на ASP.NET MVC, управление базой данных — на SQL Server. Выполнена SEO-оптимизация и улучшение производительности.',
    proj2_title: 'Корпоративные веб-приложения — Real Solutions',
    proj2_desc:
      'Backend-сервисы разработаны на .NET Core Web API, frontend-интерфейсы — на Angular. Спроектирована масштабируемая архитектура для корпоративных проектов, проводилось наставничество junior-разработчиков. Реализованы интеграции API, контроль безопасности и улучшение производительности.',
    proj3_title: 'Программные решения для МСБ — Freelance',
    proj3_desc:
      'Разработаны индивидуальные программные решения для малого и среднего бизнеса. Завершены веб-приложения, интеграции API и проекты по обработке данных. Спроектированы удобные интерфейсы согласно требованиям клиентов.',
    proj4_title: 'Корпоративные веб-приложения — E2 Technologies',
    proj4_desc:
      'Backend и frontend разработка для корпоративных веб-приложений. Проектирование баз данных и улучшение производительности. Разработка RESTful API и интеграция со сторонними системами.',

    exp_label: 'Опыт работы',
    exp_heading_1: 'Места,',
    exp_heading_2: 'где я работал.',
    exp_current_badge: 'Последняя должность',
    exp_fulltime: 'Полная занятость',
    exp_hybrid: 'Гибрид',
    exp_freelance: 'Фриланс',
    exp_istanbul: 'Стамбул',
    exp_duzce: 'Дюздже',

    exp1_role: 'Специалист по разработке ПО',
    exp1_desc:
      'Созданы backend-сервисы на .NET Core Web API, разработаны frontend-интерфейсы на Angular. Спроектированы масштабируемые архитектуры с использованием современных веб-технологий для корпоративных проектов. Проводилось наставничество junior-разработчиков и координация команды. Реализованы интеграции API, контроль безопасности и улучшение производительности.',
    exp2_role: 'Freelance-разработчик',
    exp2_desc:
      'Разработаны индивидуальные программные решения для малого и среднего бизнеса. Завершены веб-приложения, интеграции API и проекты по обработке данных. Спроектированы удобные интерфейсы согласно требованиям клиентов.',
    exp3_role: 'Full Stack разработчик',
    exp3_desc:
      'Backend-разработка на ASP.NET MVC, управление базами данных на SQL Server, создание цифровой медиаплатформы «Diriliş Ertuğrul». Координация команды через управление проектами и Agile-методологии. Проведена SEO-оптимизация и работы по улучшению производительности.',
    exp4_role: 'Full Stack разработчик',
    exp4_desc:
      'Backend и frontend разработка для корпоративных веб-приложений. Проектирование баз данных и улучшение производительности. Разработка RESTful API и интеграция со сторонними системами.',
    exp5_role: 'Frontend-разработчик',
    exp5_desc:
      'Разработка и оптимизация веб-интерфейсов. Создание адаптивных дизайнов с использованием HTML/CSS и JavaScript. Участие в поддержке и исправлении ошибок в существующих проектах.',

    contact_label: 'Контакты',
    contact_heading_1: 'Давайте создадим',
    contact_heading_2: 'что-то вместе.',
    contact_subtitle:
      'Будь то постоянная должность, freelance-проект или просто разговор об интересных задачах — жду вашего сообщения.',
    contact_quote:
      '«Имея 14-летний опыт, я ищу роли, где смогу принимать значимые технические решения, работать с сильной командой и создавать то, чем люди действительно пользуются. Базируюсь в Дюздже, открыт к гибридной работе.»',
    contact_available: 'Открыт к предложениям',
    contact_response: 'Обычно отвечает в течение 24 часов. Местоположение: Дюздже, Турция.',
    contact_location: 'Дюздже, Турция',
    contact_cv_handle: 'Скачать PDF',

    footer_copyright: '© 2026 Kerim Şahin',
  },

  es: {
    nav_projects: 'Proyectos',
    nav_experience: 'Experiencia',
    nav_contact: 'Contacto',
    nav_cv: 'CV',

    hero_greeting: 'Hola, soy',
    hero_title: 'Kerim Şahin',
    hero_subtitle: 'Desarrollador Senior Backend & Frontend',
    hero_bio:
      'Desarrollador Full Stack con 14 años de experiencia en desarrollo de software, especializado en C#, .NET Core, Blazor, Angular y JavaScript. Amplio conocimiento en desarrollo SaaS, diseño de bases de datos, integración de APIs y arquitectura de microservicios. Sólidas habilidades en liderazgo de equipos, gestión ágil de proyectos y mentoría.',
    hero_cta_projects: 'Ver proyectos',
    hero_cta_contact: 'Contactar',
    hero_stat_years: 'Años de experiencia',
    hero_stat_companies: 'Empresas',
    hero_stat_stack: 'Full Stack',
    hero_stat_stack_sub: 'Backend & Frontend',
    hero_open: 'Abierto a oportunidades',

    skills_label: 'Stack Tecnológico',
    skills_heading_1: 'Herramientas con las',
    skills_heading_2: 'que trabajo.',
    skills_subtitle:
      'Tecnologías y herramientas que prefiero al desarrollar sistemas en producción.',
    skills_backend_title: 'Desarrollo Backend',
    skills_backend_desc:
      '.NET Core Web API, arquitectura de microservicios, Entity Framework, diseño de APIs RESTful y optimización del rendimiento.',
    skills_frontend_title: 'Desarrollo Frontend',
    skills_frontend_desc:
      'Interfaces modernas y amigables con Angular, Blazor y React. Diseño responsivo y desarrollo orientado al rendimiento.',
    skills_db_title: 'Bases de Datos & DevOps',
    skills_db_desc:
      'Diseño de bases de datos con SQL Server, PostgreSQL, MongoDB y Redis. Pipelines CI/CD con Docker, Azure DevOps y Git.',

    projects_label: 'Trabajos Destacados',
    projects_heading_1: 'Proyectos que he',
    projects_heading_2: 'desarrollado.',
    projects_view_linkedin: 'Ver en LinkedIn',
    projects_featured: 'Destacado',
    projects_status_done: 'Completado',
    projects_status_active: 'Activo',

    proj1_title: 'Plataforma de Medios Digitales Diriliş Ertuğrul',
    proj1_desc:
      'Plataforma de medios digitales para la serie Diriliş Ertuğrul, desarrollada en MOPP Agency. Backend construido con ASP.NET MVC, gestión de base de datos con SQL Server. Se implementaron optimizaciones SEO y mejoras de rendimiento.',
    proj2_title: 'Aplicaciones Web Corporativas — Real Solutions',
    proj2_desc:
      'Servicios backend desarrollados con .NET Core Web API, interfaces frontend con Angular. Arquitectura escalable diseñada para proyectos corporativos, mentoría a desarrolladores junior. Integraciones de API, controles de seguridad y mejoras de rendimiento entregadas.',
    proj3_title: 'Soluciones de Software para PYMEs — Freelance',
    proj3_desc:
      'Soluciones de software personalizadas desarrolladas para pequeñas y medianas empresas. Aplicaciones web, integraciones de API y proyectos de procesamiento de datos completados. Interfaces amigables diseñadas según las necesidades del cliente.',
    proj4_title: 'Aplicaciones Web Corporativas — E2 Technologies',
    proj4_desc:
      'Desarrollo backend y frontend para aplicaciones web corporativas. Diseño de bases de datos y mejoras de rendimiento. Desarrollo de APIs RESTful e integraciones con sistemas de terceros.',

    exp_label: 'Experiencia',
    exp_heading_1: 'Lugares donde',
    exp_heading_2: 'he trabajado.',
    exp_current_badge: 'Último puesto',
    exp_fulltime: 'Tiempo completo',
    exp_hybrid: 'Híbrido',
    exp_freelance: 'Freelance',
    exp_istanbul: 'Estambul',
    exp_duzce: 'Düzce',

    exp1_role: 'Especialista en Desarrollo de Software',
    exp1_desc:
      'Servicios backend creados con .NET Core Web API, interfaces frontend desarrolladas con Angular. Arquitecturas escalables diseñadas con tecnologías web modernas para proyectos corporativos. Coordinación de equipo gestionada con mentoría a desarrolladores junior. Integraciones de API, controles de seguridad y mejoras de rendimiento entregadas.',
    exp2_role: 'Desarrollador Freelance',
    exp2_desc:
      'Soluciones de software personalizadas desarrolladas para pequeñas y medianas empresas. Aplicaciones web, integraciones de API y proyectos de procesamiento de datos completados. Interfaces amigables diseñadas según las necesidades del cliente.',
    exp3_role: 'Desarrollador Full Stack',
    exp3_desc:
      'Desarrollo backend con ASP.NET MVC, gestión de bases de datos con SQL Server y desarrollo de la Plataforma de Medios Digitales Diriliş Ertuğrul. Coordinación de equipo mediante gestión de proyectos y metodologías Agile. Trabajos de optimización SEO y rendimiento realizados.',
    exp4_role: 'Desarrollador Full Stack',
    exp4_desc:
      'Desarrollo backend y frontend para aplicaciones web corporativas. Diseño de bases de datos y mejoras de rendimiento implementadas. Desarrollo de APIs RESTful e integraciones con sistemas de terceros.',
    exp5_role: 'Desarrollador Frontend',
    exp5_desc:
      'Interfaces web desarrolladas y optimizadas. Diseños responsivos creados con HTML/CSS y JavaScript. Contribución a tareas de mantenimiento y corrección de errores en proyectos existentes.',

    contact_label: 'Contacto',
    contact_heading_1: 'Construyamos algo',
    contact_heading_2: 'juntos.',
    contact_subtitle:
      'Ya sea un puesto a tiempo completo, un proyecto freelance o simplemente una conversación sobre problemas interesantes — espero tu mensaje.',
    contact_quote:
      '"Con 14 años de experiencia, me enfoco en roles donde pueda tomar decisiones técnicas significativas, trabajar con un equipo sólido y construir cosas que la gente realmente use. Basado en Düzce, abierto al trabajo híbrido."',
    contact_available: 'Abierto a oportunidades',
    contact_response: 'Generalmente responde en 24 horas. Ubicación: Düzce, Turquía.',
    contact_location: 'Düzce, Turquía',
    contact_cv_handle: 'Descargar PDF',

    footer_copyright: '© 2026 Kerim Şahin',
  },
};

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: Translations;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguage] = useState<Language>('tr');

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t: translations[language] }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage(): LanguageContextType {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within LanguageProvider');
  return ctx;
}
